import {
  CreatorPermissionsApiService,
  DashboardRoleRestEndpointService,
  InviteMemberForm,
  TeamApiService,
  type TeamMemberDto,
} from '@api'

import { LocalStorageKeys } from '@/consts/localStorage.ts'
import type { UpdateCreatorAccessRequest } from '@/modules/creators/types.ts'
import type {
  TeamCreateRoleRequest,
  TeamInviteMemberRequest,
  TeamRolePermissionAssignmentRequest,
} from '@/modules/team/types.ts'

const getTeam = async () => {
  return TeamApiService.listMembers()
}

const changeUserById = async (newUserData: Partial<TeamMemberDto>) => {
  if (!newUserData.id) {
    throw new Error('Member ID is required')
  }

  return TeamApiService.updateMember({
    id: newUserData.id,
    requestBody: newUserData,
  })
}

const removeUserById = async (id: number): Promise<void> => {
  await TeamApiService.deleteMember({ id })
}

/**
 * Invites a new member in a team
 * @description An invitation email will be sent upon successful creation.
 */
const inviteUser = async (
  inviteUserData: InviteMemberForm[],
): Promise<TeamMemberDto[]> => {
  return TeamApiService.bulkInviteMembers({
    requestBody: inviteUserData,
  })
}

const updateCreatorAccess = (
  creatorId: number,
  data: UpdateCreatorAccessRequest,
) => {
  return CreatorPermissionsApiService.setAllPermissions({
    creatorId,
    requestBody: data,
  })
}

const getAvailableCreatorPermissions = async () => {
  return CreatorPermissionsApiService.getAllCreatorPermissions()
}

const getTeamRoles = async () => {
  return DashboardRoleRestEndpointService.getRoles()
}

const deleteTeamRole = async (id: number) => {
  return DashboardRoleRestEndpointService.deleteRole({ id })
}

const createNewRole = async (body: TeamCreateRoleRequest) => {
  return DashboardRoleRestEndpointService.createRole({
    requestBody: body,
  })
}

const editRole = async (
  body: Partial<TeamCreateRoleRequest>,
  roleId: number,
) => {
  return DashboardRoleRestEndpointService.updateRole({
    id: roleId,
    requestBody: body,
  })
}

const getTeamRole = async (roleId: number) => {
  return DashboardRoleRestEndpointService.getRole({ id: roleId })
}

const getAllRolePermissions = async () => {
  const response = await DashboardRoleRestEndpointService.getPermissions()

  const shouldShowAllPermissions = localStorage.getItem(
    LocalStorageKeys.SHOW_ALL_PERMISSIONS,
  )

  return response.filter(
    (permission) => shouldShowAllPermissions || permission.display,
  )
}

const assignPermissionsToRole = async (
  body: TeamRolePermissionAssignmentRequest,
  roleId: number,
) => {
  return DashboardRoleRestEndpointService.updateRole({
    id: roleId,
    requestBody: body,
  })
}

const assignRolesToMember = async (memberId: number, roleIds: number[]) => {
  return DashboardRoleRestEndpointService.setRoles({
    teamMemberId: memberId,
    requestBody: { roleIds },
  })
}

export const teamService = {
  getTeam,
  changeUserById,
  removeUserById,
  inviteUser,
  updateCreatorAccess,
  getAvailableCreatorPermissions,
  getTeamRoles,
  getTeamRole,
  getAllRolePermissions,
  deleteTeamRole,
  createNewRole,
  editRole,
  assignPermissionsToRole,
  assignRolesToMember,
}
